import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Diamond, Eye, EyeOff, LogIn, ShieldCheck, TrendingUp, Package, FileText, AlertCircle } from 'lucide-react';

const FEATURES = [
  { icon: TrendingUp, text: 'Real-time Sales & Purchase Tracking' },
  { icon: Package,    text: 'Live Inventory & Stock Ledger' },
  { icon: FileText,   text: 'GST-Compliant Invoice Management' },
  { icon: ShieldCheck,text: 'Multi-Branch & Role-Based Access' },
];

export default function LoginPage() {
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const [form, setForm]     = useState({ username: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      const msg = 'Please enter both username and password';
      setError(msg);
      toast.error(msg);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(form.username, form.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data?.Message || err.message;
      const errorMsg = serverMsg || 'Password or username is wrong';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp">
      {/* ── Left Panel ── */}
      <div className="lp-left">
        <div className="lp-brand">
          <div className="lp-logo" style={{ padding: '0', background: 'transparent', border: 'none', boxShadow: 'none', width: 48, height: 48, borderRadius: 8, overflow: 'hidden' }}>
            <img src="/carat-quest-logo.png" alt="Carat Quest" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }} />
          </div>
          <div>
            <div className="lp-brand-name">CARAT QUEST</div>
            <div className="lp-brand-tag">Silver Inventory System</div>
          </div>
        </div>

        <div className="lp-hero">
          <h1>Manage your silver business with precision.</h1>
          <p>Everything you need — inventory, invoicing, purchases, and GST — in one professional platform.</p>
        </div>

        <ul className="lp-features">
          {FEATURES.map(({ icon: Icon, text }) => (
            <li key={text} className="lp-feature">
              <span className="lp-feature-icon"><Icon size={14} /></span>
              {text}
            </li>
          ))}
        </ul>

        <div className="lp-orb lp-orb-1" />
        <div className="lp-orb lp-orb-2" />

        <div className="lp-left-footer">Powered by LetinAI Solutions</div>
      </div>

      {/* ── Right Panel ── */}
      <div className="lp-right">
        <div className="lp-form-card">
          <div className="lp-form-header">
            <div className="lp-form-icon" style={{ padding: '0', background: 'transparent', border: 'none', width: 64, height: 64, margin: '0 auto 12px', borderRadius: 10, overflow: 'hidden' }}>
              <img src="/carat-quest-logo.png" alt="Carat Quest" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 10 }} />
            </div>
            <h2>Welcome back</h2>
            <p>Sign in to your CARAT QUEST account</p>
          </div>

          {error && (
            <div className="lp-error-alert" style={{
              padding: '10px 14px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '0.85rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="lp-form">
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                className="form-control"
                placeholder="Enter your username"
                value={form.username}
                onChange={e => {
                  setError('');
                  setForm({ ...form, username: e.target.value });
                }}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={e => {
                    setError('');
                    setForm({ ...form, password: e.target.value });
                  }}
                  autoComplete="current-password"
                  style={{ paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="lp-pwd-toggle"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg lp-submit"
              disabled={loading}
            >
              {loading ? <span className="spinner" style={{ width: 18, height: 18 }} /> : <LogIn size={18} />}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          {/* hint block removed */}
        </div>
      </div>

      <style>{`
        /* ══ Page ══════════════════════════════ */
        .lp {
          min-height: 100vh;
          display: flex;
        }

        /* ══ Left ═════════════════════════════ */
        .lp-left {
          position: relative;
          width: 440px;
          min-width: 440px;
          background: linear-gradient(155deg, #0c1a2e 0%, #0f2340 50%, #152952 100%);
          padding: 40px;
          display: flex;
          flex-direction: column;
          gap: 0;
          overflow: hidden;
        }

        /* Decorative orbs */
        .lp-orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        .lp-orb-1 {
          width: 320px; height: 320px;
          top: -80px; right: -100px;
          background: radial-gradient(circle, rgba(212,160,23,0.12) 0%, transparent 70%);
        }
        .lp-orb-2 {
          width: 280px; height: 280px;
          bottom: -60px; left: -80px;
          background: radial-gradient(circle, rgba(42,80,128,0.35) 0%, transparent 70%);
        }

        /* Brand */
        .lp-brand {
          display: flex; align-items: center; gap: 14px;
          margin-bottom: 52px;
          position: relative; z-index: 1;
        }
        .lp-logo {
          width: 48px; height: 48px;
          background: #000000;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          box-shadow: none;
        }
        .lp-brand-name { font-size: 1.5rem; font-weight: 800; color: #fff; letter-spacing: 0.05em; line-height: 1; }
        .lp-brand-tag  { font-size: 0.65rem; color: rgba(255,255,255,0.38); text-transform: uppercase; letter-spacing: 0.12em; margin-top: 3px; }

        /* Hero */
        .lp-hero {
          position: relative; z-index: 1;
          margin-bottom: 32px;
        }
        .lp-hero h1 {
          font-size: 1.75rem; font-weight: 800; color: #fff;
          line-height: 1.25; letter-spacing: -0.02em;
          margin-bottom: 14px;
        }
        .lp-hero p {
          color: rgba(255,255,255,0.5);
          font-size: 0.9375rem;
          line-height: 1.7;
        }

        /* Features */
        .lp-features {
          position: relative; z-index: 1;
          list-style: none;
          display: flex; flex-direction: column; gap: 12px;
          flex: 1;
        }
        .lp-feature {
          display: flex; align-items: center; gap: 12px;
          color: rgba(255,255,255,0.65);
          font-size: 0.9rem;
          font-weight: 400;
        }
        .lp-feature-icon {
          width: 28px; height: 28px;
          background: rgba(212,160,23,0.15);
          border-radius: 7px;
          display: flex; align-items: center; justify-content: center;
          color: var(--accent-400);
          flex-shrink: 0;
        }

        .lp-left-footer {
          position: relative; z-index: 1;
          font-size: 0.72rem;
          color: rgba(255,255,255,0.22);
          margin-top: 32px;
          letter-spacing: 0.02em;
        }

        /* ══ Right ════════════════════════════ */
        .lp-right {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 24px;
          background: var(--bg);
        }

        .lp-form-card {
          width: 100%;
          max-width: 400px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-xl);
          padding: 36px;
          box-shadow: var(--shadow-lg);
          animation: modalIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .lp-form-header { text-align: center; margin-bottom: 28px; }
        .lp-form-icon {
          width: 52px; height: 52px;
          background: linear-gradient(135deg, var(--primary-600), var(--primary-500));
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          margin: 0 auto 16px;
          box-shadow: 0 8px 20px rgba(30,58,95,0.25);
        }
        .lp-form-header h2 { font-size: 1.375rem; font-weight: 700; color: var(--text-primary); margin-bottom: 6px; }
        .lp-form-header p  { font-size: 0.9rem; color: var(--text-muted); }

        .lp-form { display: flex; flex-direction: column; gap: 18px; margin-bottom: 20px; }

        .lp-submit { width: 100%; justify-content: center; padding: 11px 20px; font-size: 0.9375rem; }

        .lp-pwd-toggle {
          position: absolute; right: 11px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: var(--text-muted);
          display: flex; align-items: center;
          padding: 2px;
          transition: color var(--transition-fast);
        }
        .lp-pwd-toggle:hover { color: var(--text-primary); }

        .lp-hint {
          display: flex; align-items: center; gap: 7px;
          padding: 10px 14px;
          background: var(--accent-50);
          border: 1px solid var(--accent-100);
          border-radius: var(--radius-sm);
          font-size: 0.8rem;
          color: var(--warning-dark);
        }
        .lp-hint code {
          background: rgba(0,0,0,0.06);
          padding: 1px 6px; border-radius: 4px;
          font-family: var(--mono); font-size: 0.78rem;
        }

        /* ══ Responsive ════════════════════════ */
        @media (max-width: 900px) {
          .lp-left { width: 360px; min-width: 360px; }
        }
        @media (max-width: 768px) {
          .lp { flex-direction: column; }
          .lp-left { width: 100%; min-width: unset; padding: 28px 24px; }
          .lp-hero h1 { font-size: 1.4rem; }
          .lp-features { display: none; }
          .lp-right { padding: 28px 20px; }
          .lp-form-card { padding: 24px; }
        }
      `}</style>
    </div>
  );
}
